const REDACTED = "[redacted]";
const MAX_STRING_LENGTH = 500;
const MAX_ARRAY_ITEMS = 20;
const MAX_DEPTH = 6;

const SENSITIVE_KEY_PATTERNS = [
  "authorization",
  "cookie",
  "jwt",
  "password",
  "secret",
  "session",
  "token",
];

// PII key patterns matched against lowercased keys with underscores stripped,
// so each pattern covers both snake_case and camelCase variants
// (e.g. "fechanacimiento" matches fecha_nacimiento and fechaNacimiento).
const PII_KEY_PATTERNS = [
  "curp",
  "rfc",
  "nombre", // also covers segundo_nombre
  "apellidopaterno",
  "apellidomaterno",
  "firstname",
  "givenname", // covers given_names
  "fullname",
  "lastname", // covers paternal_last_name / maternal_last_name
  "surname", // covers paternal_surname / maternal_surname / first_surname
  "dateofbirth",
  "fechanacimiento",
  "claveelector",
  "electorkey",
  "address",
  "direccion",
  "calle",
  "street",
  "zip", // covers zip_code
  "codigopostal",
  "clabe", // covers clabe_account
  "mrzraw",
  "accountholder",
];

const FILE_KEYS = new Set([
  "back_image",
  "file",
  "files",
  "front_image",
  "image",
]);

export const verificationDebugLogsEnabled =
  import.meta.env.VITE_VERIFICATION_DEBUG_LOGS === "true";

const verificationDebugPayloadsEnabled =
  import.meta.env.VITE_VERIFICATION_DEBUG_LOG_PAYLOADS === "true";

export function verificationDebugLog(
  event: string,
  context?: Record<string, unknown>,
  payload?: unknown,
) {
  if (!verificationDebugLogsEnabled) return;

  const data: Record<string, unknown> = {
    event,
    context: redactForVerificationDebug(context ?? {}),
  };

  if (payload !== undefined && verificationDebugPayloadsEnabled) {
    data.payload = redactForVerificationDebug(payload);
  }

  console.log("[verification-debug]", data);
}

export function verificationDebugError(
  event: string,
  error: unknown,
  context?: Record<string, unknown>,
) {
  if (!verificationDebugLogsEnabled) return;

  console.error("[verification-debug]", {
    event,
    context: redactForVerificationDebug(context ?? {}),
    error: normalizeError(error),
  });
}

export function summarizeFormData(formData: FormData) {
  return Array.from(formData.entries()).map(([key, value]) => {
    if (value instanceof File) {
      return {
        key,
        filename: value.name,
        size: value.size,
        type: value.type,
      };
    }

    return { key, value };
  });
}

export function summarizeVerificationResponse(payload: unknown) {
  if (!payload || typeof payload !== "object") {
    return { type: typeof payload };
  }

  const data = payload as Record<string, unknown>;
  const finalDecision = data.final_decision as
    | Record<string, unknown>
    | undefined;
  const verificationDebug = summarizeVerificationDebug(data.verification_debug);

  return {
    keys: Object.keys(data),
    documentType: data.document_type,
    status: data.status,
    isValid: data.is_valid,
    invalidReason: data.invalid_reason,
    authenticityScore: data.authenticity_score ?? finalDecision?.overall_score,
    confidence:
      finalDecision?.overall_score ??
      data.confidence ??
      data.overall_confidence ??
      data.authenticity_score,
    extractedDataKeys: objectKeys(data.extracted_data),
    extractedAddressKeys: objectKeys(data.extracted_address),
    validatedAttributeKeys: objectKeys(data.validated_attributes),
    dataIntegrityKeys: objectKeys(data.data_integrity),
    validationDetailKeys: objectKeys(data.validation_details),
    finalDecision: finalDecision
      ? {
          overallScore: finalDecision.overall_score,
          riskLevel: finalDecision.risk_level,
          safeToProceed: finalDecision.safe_to_proceed,
          humanReviewRequired: finalDecision.human_review_required,
          rejectionReasons: finalDecision.rejection_reasons,
        }
      : undefined,
    verificationDebug,
  };
}

export function summarizeCombinedVerificationData(payload: unknown) {
  if (!payload || typeof payload !== "object") {
    return { type: typeof payload };
  }

  const data = payload as Record<string, unknown>;
  const ocr = recordAt(data, "ocr");
  const extractedData = recordAt(data, "extracted_data");
  const extractedAddress = recordAt(extractedData ?? {}, "direccion");
  return {
    keys: Object.keys(data),
    hasValidator: Boolean(data.validator),
    hasOcr: Boolean(ocr),
    hasExtractedData: Boolean(extractedData),
    ocrKeys: objectKeys(ocr),
    extractedDataKeys: objectKeys(extractedData),
    addressKeys: objectKeys(extractedAddress),
  };
}

function objectKeys(value: unknown) {
  return value && typeof value === "object" ? Object.keys(value) : undefined;
}

function summarizeVerificationDebug(value: unknown) {
  if (!isRecord(value)) return undefined;

  const localEnrichment = recordAt(value, "local_enrichment_decision");
  const deterministicChecks = recordAt(value, "deterministic_checks");
  const publicSourceChecks = recordAt(value, "public_source_checks");
  const officialVerification = recordAt(value, "official_verification");
  const validationMetadata = recordAt(value, "validation_metadata");

  return {
    keys: Object.keys(value),
    localEnrichment: localEnrichment
      ? {
          action: localEnrichment["action"],
          reasons: localEnrichment["reasons"],
        }
      : undefined,
    deterministicCheckKeys: objectKeys(deterministicChecks),
    publicSourceChecks: publicSourceChecks
      ? {
          status: publicSourceChecks["status"],
          enabled: publicSourceChecks["enabled"],
          reason: publicSourceChecks["reason"],
        }
      : undefined,
    officialVerification: officialVerification
      ? {
          status: officialVerification["status"],
          source: officialVerification["source"],
          reason: officialVerification["reason"] ?? officialVerification["details"],
        }
      : undefined,
    validationMetadata: validationMetadata
      ? {
          validationDate: validationMetadata["validation_date"],
          officialVerificationPerformed:
            validationMetadata["official_verification_performed"],
          publicSourceChecksEnabled:
            validationMetadata["public_source_checks_enabled"],
        }
      : undefined,
  };
}

function recordAt(
  value: Record<string, unknown>,
  key: string,
): Record<string, unknown> | undefined {
  const nestedValue = value[key];
  return isRecord(nestedValue) ? nestedValue : undefined;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function normalizeError(error: unknown) {
  if (!error || typeof error !== "object") {
    return { message: String(error) };
  }

  const maybeError = error as {
    message?: string;
    response?: {
      status?: number;
      data?: unknown;
    };
  };

  return {
    message: maybeError.message,
    status: maybeError.response?.status,
    responseData: redactForVerificationDebug(maybeError.response?.data),
  };
}

export function redactForVerificationDebug(
  value: unknown,
  depth = 0,
  key?: string,
): unknown {
  if (key) {
    const normalizedKey = key.toLowerCase();
    const compactKey = normalizedKey.replace(/_/g, "");
    if (
      FILE_KEYS.has(normalizedKey) ||
      SENSITIVE_KEY_PATTERNS.some((pattern) =>
        normalizedKey.includes(pattern),
      ) ||
      PII_KEY_PATTERNS.some((pattern) => compactKey.includes(pattern))
    ) {
      return REDACTED;
    }
  }

  if (depth >= MAX_DEPTH) return "[truncated]";

  if (value instanceof File) {
    return {
      filename: value.name,
      size: value.size,
      type: value.type,
    };
  }

  if (typeof value === "string") {
    return value.length > MAX_STRING_LENGTH
      ? `${value.slice(0, MAX_STRING_LENGTH)}...`
      : value;
  }

  if (!value || typeof value !== "object") {
    return value;
  }

  if (Array.isArray(value)) {
    const items = value
      .slice(0, MAX_ARRAY_ITEMS)
      .map((item) => redactForVerificationDebug(item, depth + 1));
    if (value.length > MAX_ARRAY_ITEMS) {
      items.push(`[truncated: ${value.length - MAX_ARRAY_ITEMS} more items]`);
    }
    return items;
  }

  return Object.fromEntries(
    Object.entries(value as Record<string, unknown>).map(
      ([entryKey, entryValue]) => [
        entryKey,
        redactForVerificationDebug(entryValue, depth + 1, entryKey),
      ],
    ),
  );
}
